import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Job } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { useCampaignLabels } from "../lib/campaignLabels";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { ListShell } from "../components/ListShell";
import { orAbsent, shortDate } from "../lib/format";
import { downloadNarrowed } from "../lib/csvDownload";
import { applyRules } from "../lib/advancedFilters";
import { useCsvColumns } from "../lib/columnChoice";
import { DownloadJobFile } from "../components/DownloadJobFile";

/* Everything long-running, in one list.
 *
 * Campaign runs, exports, enrichment sweeps and vendor briefs are one mechanism
 * in the engine, so they are one screen here.
 *
 * Two things this screen refuses to do. It does not imply minutes: enrichment
 * fetches pages far faster than they are read, about 1,500 an hour against 150
 * an hour of reading, so a queued job can sit for hours and saying otherwise is
 * worse than saying nothing. And it never invents a reason for a failure,
 * because the database guarantees a failed job carries one. */
/* Words for the kinds of lake.job, every kind its CHECK allows (engine
   migration 149). A kind missing here shows as the raw word and cannot be
   picked out of the Kind menu, which is how five kinds read until 2026-10-05. */
const KIND_WORDS: Record<string, string> = {
  campaign_run: "Campaign run",
  lead_sheet: "Lead Sheet",
  crm_export: "CRM export",
  orum_export: "Orum list",
  enrichment_sweep: "Web enrichment",
  file_import: "File import",
  apollo_fill: "Apollo enrichment, one record",
  apollo_phone: "Apollo enrichment, phone",
  review_merge: "Duplicate combined",
  review_undo: "Duplicate undone",
  process_capture: "Page capture",
  campaign_enrichment: "Automatic web enrichment",
  apollo_campaign: "Apollo enrichment",
  thin_export: "Thin-records file",
  notice: "Notice",
  campaign_measure: "Campaign measured",
};

const STATUS_WORDS: Record<string, { label: string; detail: string }> = {
  queued: {
    label: "Waiting",
    detail: "In the queue. This can take hours.",
  },
  running: { label: "Running", detail: "A worker has picked this up." },
  done: { label: "Done", detail: "Finished without error." },
  failed: { label: "Failed", detail: "Stopped. The reason is on the row." },
  cancelled: { label: "Cancelled", detail: "Stopped before it finished." },
};

/* The fields a rep wants first. Everything else the view returns can be
   added from the Columns control, and the choice is remembered. */
const CSV_DEFAULT = ["kind", "status", "requested_by", "queued_at", "started_at", "finished_at", "error"] as const;

export function Jobs({ session }: { session: SignedIn }) {
  /* Newest first. This list opened on the oldest job in the queue, so a rep
     who pressed Run and followed the link here had to scroll past every job
     since 10 September to find the one they had just queued. */
  const params = useListParams("queued_at", ["status", "kind"] as const, false, "job");
  const csvColumns = useCsvColumns("jobs", "job", CSV_DEFAULT);
  const queryClient = useQueryClient();

  const labels = useCampaignLabels(session.idToken);

  const query = useQuery({
    queryKey: ["jobs", params.page, params.sort, params.ascending, params.filters, params.search],
    // A running job changes without anybody pressing anything, so this one
    // screen polls. Every other screen does not, because a list must not
    // reorder under somebody mid-work.
    refetchInterval: 15_000,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      let q = apiClient(session.idToken)
        .from("job")
        .select("id,kind,status,params,requested_by,progress,result,artifact,artifact_key,error,attempts,queued_at,started_at,finished_at,produced_count,produced_noun",
                { count: "exact" });
      if (params.filters["status"]) q = q.eq("status", params.filters["status"]);
      if (params.filters["kind"]) q = q.eq("kind", params.filters["kind"]);
      /* Search what the For column shows, which is the campaign or batch this
         job was for. A job names that in one of three shapes: criteria_set is
         a string, criteria_sets is an array, and an enrichment sweep uses
         batch. Searching one of the three would quietly miss the other two.

         criteria_sets is matched as the JSON text of the array, which finds a
         slug inside it without the screen having to know how many are in
         there.

         The search is the slug rather than the label, because the slug is what
         the job stores. A rep typing "California" finds california-best-leads
         and california-chargepoint-replacement, which is what they meant. */
      const term = params.search.trim();
      if (term) {
        const safe = term.replace(/[(),*]/g, " ").trim();
        if (safe) {
          q = q.or(`params->>criteria_set.ilike.*${safe}*,`
                 + `params->>batch.ilike.*${safe}*,`
                 + `params->>criteria_sets.ilike.*${safe}*`);
        }
      }
      q = applyRules(q, "job", params.advanced);
      const { data, error, count } = await q
        .order(params.sort, { ascending: params.ascending, nullsFirst: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as Job[], total: count ?? null };
    },
  });

  const rows = query.data?.rows ?? [];

  return (
    <ListShell
      title="Jobs"
      view="job"
      searchLabel="Campaign or batch"
      params={params}
      csvColumns={csvColumns}
      total={query.data?.total ?? null}
      rowCount={rows.length}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error as Error | null}
      dataUpdatedAt={query.dataUpdatedAt}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["jobs"] })}
      onRetry={() => query.refetch()}
      onDownload={() =>
        downloadNarrowed({
          name: "jobs",
          columns: csvColumns.chosen,
          total: query.data?.total ?? null,
          fetchAll: async (ceiling) => {
            let q = apiClient(session.idToken).from("job").select(csvColumns.chosen.join(","));
            if (params.filters["status"]) q = q.eq("status", params.filters["status"]);
            if (params.filters["kind"]) q = q.eq("kind", params.filters["kind"]);
      q = applyRules(q, "job", params.advanced);
            const { data, error } = await q
              .order(params.sort, { ascending: params.ascending, nullsFirst: false })
              .range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            // The select is built from the person's column choice, so its shape
            // is not known while compiling. The rows are what the view returns
            // either way.
            return (data ?? []) as unknown as Job[];
          },
        })
      }
      emptyEverything="No jobs yet. Campaign runs and exports appear here."
      filters={
        <>
          <label htmlFor="f-status">Status</label>
          <select id="f-status" value={params.filters["status"] ?? ""}
                  onChange={(e) => params.set({ filters: { ...params.filters, status: e.target.value } })}>
            <option value="">any</option>
            {Object.entries(STATUS_WORDS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <label htmlFor="f-kind">Type</label>
          <select id="f-kind" value={params.filters["kind"] ?? ""}
                  onChange={(e) => params.set({ filters: { ...params.filters, kind: e.target.value } })}>
            <option value="">any</option>
            {Object.entries(KIND_WORDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </>
      }
    >
      <table>
        <thead>
          <tr>
            <th>What</th><th>For</th><th>Status</th><th>Asked by</th>
            <th>Queued</th><th>Finished</th><th>Result</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((j) => {
            const s = j.status ? STATUS_WORDS[j.status] : undefined;
            const p = (j.params ?? {}) as Record<string, unknown>;
            const say = (slug: string) => labels.data?.get(slug) ?? slug;
            const forWhat =
              (Array.isArray(p["criteria_sets"]) ? (p["criteria_sets"] as string[]).map(say).join(", ") : null) ??
              (typeof p["criteria_set"] === "string" ? say(p["criteria_set"] as string) : null) ??
              (Array.isArray(p["states"]) ? (p["states"] as string[]).join(", ") : null);
            return (
              <tr key={j.id}>
                <td>{j.kind ? (KIND_WORDS[j.kind] ?? j.kind) : orAbsent(j.kind)}</td>
                <td>{forWhat ?? <span className="muted">not named</span>}</td>
                <td title={s?.detail}>{s?.label ?? j.status}</td>
                <td className="muted">{orAbsent(j.requested_by)}</td>
                <td>{shortDate(j.queued_at)}</td>
                <td>{shortDate(j.finished_at)}</td>
                <td>
                  {j.status === "failed" ? (
                    /* Never "something went wrong". A failed job always carries
                       a reason, enforced by a CHECK in the database. */
                    <span className="refusal">{j.error ?? "failed, and no reason was recorded. Ask James."}</span>
                  ) : j.status === "done" && j.artifact_key && j.id ? (
                    <DownloadJobFile token={session.idToken} jobId={j.id} />
                  ) : j.status === "done" && j.artifact ? (
                    /* The job wrote a file and the copy to the bucket did not
                       happen. That is a different thing from a job with no file
                       at all, and the result says which. */
                    <span className="refusal" title={j.artifact}>
                      the file was made but never copied off the box, so it cannot be downloaded
                    </span>
                  ) : j.produced_count !== null && j.produced_count !== undefined ? (
                    /* What the job produced, and what that number counts.
                       Both come from api.job. The noun is read rather than
                       mapped from the kind here, because a screen that maps
                       kind to a word is a second place the two can disagree,
                       and a job kind added later would read as buildings.

                       Engine migrations 078 and 105 derive these from the lake
                       rather than from the job's captured output, which is the
                       tail of a shell script. */
                    <>{j.produced_count.toLocaleString()}{" "}
                      {j.produced_noun ?? "records"}</>
                  ) : j.progress ? (
                    <span className="muted">{j.progress}</span>
                  ) : j.status === "done" ? (
                    /* Finished, and this kind reports no single number. Say
                       that rather than "nothing reported", which reads as the
                       job having achieved nothing. On 2026-09-22 an enrichment
                       sweep that queued 3,128 records said nothing reported,
                       and engine migration 105 gave that kind a number. A kind
                       added later lands here until it gets one. */
                    <span className="muted">done, no count for this type</span>
                  ) : (
                    <span className="muted">not finished</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ListShell>
  );
}
